import type { Event } from '@cocoonspace/sdk-js/types/event'
import { useDeleteEvent } from '@cocoonspace/sdk-js/domains/events/events'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@cocoonspace/ui/components/alert-dialog'
import { Button } from '@cocoonspace/ui/components/button'
import { DateBadge } from '@cocoonspace/ui/components/cocoon/date-badge'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { ClockIcon, Trash2Icon } from 'lucide-react'

import { Price } from '~/components/price'
import { CartSheetSpaceMap } from './cart-sheet-space-map'

const DeleteEventButton = ({
	eventId,
	spaceTitle,
}: {
	eventId: string
	spaceTitle: string
}) => {
	const { t } = useLingui()
	const queryClient = useQueryClient()

	const { mutateAsync: deleteEvent } = useDeleteEvent({
		request: { params: { option: true } },
	})

	const onDelete = () => {
		return deleteEvent({ eventId }).then(() => {
			queryClient.invalidateQueries()
		})
	}

	return (
		<AlertDialog>
			<AlertDialogTrigger asChild>
				<Button
					variant='ghost'
					size='sm'
					icon
					aria-label={t`Supprimer ${spaceTitle} du panier`}
				>
					<Trash2Icon />
				</Button>
			</AlertDialogTrigger>

			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t`Supprimer l'événement`}</AlertDialogTitle>
				</AlertDialogHeader>

				<AlertDialogDescription>
					{t`Etes-vous sûr de vouloir supprimer cet événement de votre panier ?`}
				</AlertDialogDescription>

				<AlertDialogFooter>
					<AlertDialogCancel>{t`Annuler`}</AlertDialogCancel>

					<AlertDialogAction onClick={onDelete}>
						{t`Supprimer`}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

export const CartEventItem = ({ event }: { event: Event }) => {
	const { t } = useLingui()
	if (!event || !event.space) return null

	const start = dateTimeUtils(event.start_local)
		.tz(event.space?.timezone)
		.customFormat('hours')
		.replace('h00', 'h')

	const end = dateTimeUtils(event.end_local)
		.tz(event.space?.timezone)
		.customFormat('hours')
		.replace('h00', 'h')

	return (
		<div className='flex flex-row items-center gap-3'>
			<DateBadge
				day={event.start_local ?? ''}
				tz={event.space?.timezone}
				inverted
				size='sm'
			/>

			<div className='flex flex-1 flex-col truncate leading-tight'>
				<p className='truncate text-sm font-bold'>{event.space?.title}</p>
				<CartSheetSpaceMap space={event.space} />

				<p className='flex items-center gap-1 truncate text-xs'>
					<ClockIcon className='!size-3' />
					{t`de ${start} à ${end}`}
				</p>
			</div>

			<div className='flex flex-col truncate'>
				<div
					className='leading-none'
					data-testid='event-price'
				>
					<Price
						price={event.space_price}
						className='text-sm'
						taxType='ht'
					/>
				</div>
			</div>

			<DeleteEventButton
				eventId={event.id}
				spaceTitle={event.space?.title ?? ''}
			/>
		</div>
	)
}
